import {
  Banner,
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownOption,
  Spinner,
  TextArea,
  TextField
} from "@modernatx-internal/component-library";
import React from "react";

import { useTextBlockRendering } from "@/components/TextBlock/TextBlockRenderContext";
import { getNewsletterFormUnavailableMessage } from "@/content-V2/newsletterFormAvailability";
import { ContentComponentProps, NewsletterFormContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";
import { useNewsletterForm } from "@/hooks/useNewsletterForm";

const DEFAULT_SPECIALTY_OPTIONS = [
  { label: "Pharmacist/immunizer", value: "Pharmacist" },
  { label: "General practitioner/primary care physician", value: "General Practice" },
  { label: "Nurse practitioner/physician assistant", value: "Nurse Practitioner" },
  { label: "Vaccine purchaser", value: "Purchaser" },
  { label: "Other", value: "Other" }
];

const NewsletterFormUnavailableBanner: React.FC = () => {
  const { country, language } = useExperience();

  return (
    <Banner
      variant="info"
      title={
        <Box as="span" sx={{ fontSize: "font-size-5", lineHeight: "leading-2" }}>
          {getNewsletterFormUnavailableMessage(country, language)}
        </Box>
      }
    />
  );
};

export const NewsletterForm: React.FC<ContentComponentProps<NewsletterFormContent>> = ({
  temporarilyDisabled = false,
  ...props
}) =>
  temporarilyDisabled ? <NewsletterFormUnavailableBanner /> : <NewsletterFormEnabled {...props} />;

const NewsletterFormEnabled: React.FC<ContentComponentProps<NewsletterFormContent>> = ({
  buttonText,
  consentText = null,
  errorText,
  labelText,
  leadContext = {},
  layoutOptions,
  optionalConsentText = null,
  organizationOptions = [],
  privacyText = null,
  requireConsent = true,
  requireBothPurposeConsents = false,
  responsibilityOptions = [],
  showConsent = false,
  showCommercialOnlyConsent = false,
  showMedicalAndCommercialOnlyConsent = false,
  showMedicalOnlyConsent = false,
  showNames = true,
  showOrganization = false,
  showPostalCode = true,
  showRequest = false,
  showResponsibility = false,
  showSpecialty = true,
  showState = false,
  showTitle = false,
  specialtyOptions = DEFAULT_SPECIALTY_OPTIONS,
  stateOptions = [],
  submitOnlySelectedConsents = false,
  successText,
  validationText
}) => {
  const { editing } = useTextBlockRendering();
  const { error, loading, submitForm, success } = useNewsletterForm();
  const { expandedMode } = useExperience();
  const [validationErrorVisible, validationErrorVisibleSet] = React.useState(false);

  const [email, emailSet] = React.useState("");
  const [firstName, firstNameSet] = React.useState("");
  const [lastName, lastNameSet] = React.useState("");
  const [organization, organizationSet] = React.useState("");
  const [postalCode, postalCodeSet] = React.useState("");
  const [request, requestSet] = React.useState("");
  const [responsibility, responsibilitySet] = React.useState<DropdownOption["value"]>(null);
  const [specialty, specialtySet] = React.useState<DropdownOption["value"]>(null);
  const [state, stateSet] = React.useState<DropdownOption["value"]>(null);
  const [title, titleSet] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [commercialOnlyConsent, setCommercialOnlyConsent] = React.useState(false);
  const [medicalAndCommercialOnlyConsent, setMedicalAndCommercialOnlyConsent] =
    React.useState(false);
  const [medicalOnlyConsent, setMedicalOnlyConsent] = React.useState(false);

  function isValidName(s: string): boolean {
    return !!s.trim().length && NAME_REGEXP.test(s.trim());
  }
  const NAME_REGEXP = /^[\p{L}\p{N}][\p{L}\p{N}'\- ]*[\p{L}\p{N}]$/u;
  const firstNameValid = isValidName(firstName);
  const lastNameValid = isValidName(lastName);
  const emailValid = !!email.trim().length && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const postalCodeValid =
    !postalCode.trim().length || /^[A-Za-z0-9\s\-]{3,12}$/.test(postalCode.trim());
  const specialtyValid = !!specialty;
  const stateValid = !!state;
  const isEmailOnly = !(showNames || showPostalCode || showSpecialty || showState);
  const showOrganizationDropdown = organizationOptions.length > 0;
  const showsPurposeConsent =
    showCommercialOnlyConsent || showMedicalAndCommercialOnlyConsent || showMedicalOnlyConsent;
  const privacyAfterSubmit = layoutOptions?.privacyAfterSubmit ?? false;
  const stateAfterEmail = layoutOptions?.stateAfterEmail ?? false;
  const purposeConsentValid =
    !requireConsent ||
    !showsPurposeConsent ||
    (requireBothPurposeConsents
      ? (!showCommercialOnlyConsent || commercialOnlyConsent) &&
        (!showMedicalAndCommercialOnlyConsent || medicalAndCommercialOnlyConsent) &&
        (!showMedicalOnlyConsent || medicalOnlyConsent)
      : commercialOnlyConsent || medicalAndCommercialOnlyConsent || medicalOnlyConsent);
  const showConsentValidation =
    requireConsent &&
    (showConsent || showsPurposeConsent) &&
    (expandedMode || (showConsent && !consent) || !purposeConsentValid);

  const formValid =
    emailValid &&
    (showNames ? firstNameValid : true) &&
    (showNames ? lastNameValid : true) &&
    (showOrganization ? !!organization : true) &&
    (showSpecialty ? specialtyValid : true) &&
    (showState ? stateValid : true) &&
    (showPostalCode ? postalCodeValid : true) &&
    (!(showConsent && requireConsent) || consent) &&
    purposeConsentValid;

  const handleSpecialtySelect = React.useCallback((option: DropdownOption) => {
    specialtySet(option.value);
  }, []);

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!editing && formValid) {
        submitForm({
          ...leadContext,
          email,
          firstName,
          lastName,
          ...(showTitle && title && { title }),
          ...(showOrganization && { organization }),
          ...(showResponsibility && responsibility && { responsibility: responsibility as string }),
          ...(showRequest && request && { formRequest: request }),
          ...(showSpecialty && { MOD_Specialty_2__c: specialty as string }),
          ...(showState && { state: state as string }),
          ...(showPostalCode && { postalCode }),
          ...(() => {
            if (submitOnlySelectedConsents) {
              return {
                ...(showCommercialOnlyConsent && commercialOnlyConsent
                  ? { consentCommercial: true }
                  : {}),
                ...(showMedicalAndCommercialOnlyConsent && medicalAndCommercialOnlyConsent
                  ? { consentCommercial: true, consentMedical: true }
                  : {}),
                ...(showMedicalOnlyConsent && medicalOnlyConsent ? { consentMedical: true } : {})
              };
            }

            if (showMedicalAndCommercialOnlyConsent && medicalAndCommercialOnlyConsent) {
              return { consentCommercial: true, consentMedical: true };
            }

            if (showCommercialOnlyConsent && !showMedicalOnlyConsent) {
              return commercialOnlyConsent
                ? { consentCommercial: true }
                : { consentCommercial: true, consentMedical: true };
            }

            if (showCommercialOnlyConsent && showMedicalOnlyConsent) {
              if (medicalOnlyConsent && !commercialOnlyConsent) {
                return { consentMedical: true };
              } else if (commercialOnlyConsent && !medicalOnlyConsent) {
                return { consentCommercial: true };
              }
              return { consentCommercial: true, consentMedical: true };
            }

            return {};
          })()
        });
      }
    },
    [
      editing,
      formValid,
      firstName,
      lastName,
      email,
      title,
      organization,
      responsibility,
      request,
      commercialOnlyConsent,
      medicalAndCommercialOnlyConsent,
      medicalOnlyConsent,
      specialty,
      state,
      postalCode,
      showTitle,
      showOrganization,
      showResponsibility,
      showRequest,
      showCommercialOnlyConsent,
      showMedicalAndCommercialOnlyConsent,
      showMedicalOnlyConsent,
      showSpecialty,
      showState,
      showPostalCode,
      submitOnlySelectedConsents,
      leadContext,
      submitForm
    ]
  );

  const handleSubmitButtonClick = React.useCallback(() => {
    if (!formValid) {
      validationErrorVisibleSet(true);
    }
  }, [formValid]);

  const emailField = (
    <TextField
      label={labelText?.email}
      maxLength={254}
      name="Email"
      required={true}
      value={email}
      onChange={(e) => emailSet(e.currentTarget.value)}
    />
  );

  const specialtyField = showSpecialty ? (
    <Dropdown
      id="newsletter-specialty-dropdown"
      dropdownOptions={specialtyOptions}
      label={labelText?.specialty}
      required={true}
      showMenuOverride={expandedMode}
      wrapOptions={true}
      onSelectOption={handleSpecialtySelect}
    />
  ) : null;

  const stateField = showState ? (
    <Dropdown
      id="newsletter-state-dropdown"
      dropdownOptions={stateOptions}
      label={labelText?.state}
      required={true}
      showMenuOverride={expandedMode}
      wrapOptions={true}
      onSelectOption={(option) => stateSet(option.value)}
    />
  ) : null;

  return (
    <Box
      as="form"
      data-track="newsletter-form"
      data-track-form-validated={formValid}
      sx={{ position: "relative" }}
      onSubmit={handleSubmit}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          opacity: loading ? 0.2 : 1,
          transition: "transition-color-slow"
        }}
      >
        {(expandedMode || (!formValid && validationErrorVisible)) && (
          <Banner
            variant="critical"
            title={validationText?.title}
            text={
              <Box as="ul" sx={{ paddingInlineStart: 3 }}>
                {showNames && (!firstNameValid || expandedMode) && (
                  <Box as="li">{validationText?.firstName}</Box>
                )}
                {showNames && (!lastNameValid || expandedMode) && (
                  <Box as="li">{validationText?.lastName}</Box>
                )}
                {showOrganization && (!organization || expandedMode) && (
                  <Box as="li">{validationText?.organization}</Box>
                )}
                {showSpecialty && (!specialtyValid || expandedMode) && (
                  <Box as="li">{validationText?.specialty}</Box>
                )}
                {showState && (!stateValid || expandedMode) && (
                  <Box as="li">{validationText?.state}</Box>
                )}
                {(!emailValid || expandedMode) && <Box as="li">{validationText?.email}</Box>}
                {showPostalCode && (!postalCodeValid || expandedMode) && (
                  <Box as="li">{validationText?.zipCode}</Box>
                )}
                {showConsentValidation && <Box as="li">{validationText?.consentCheckbox}</Box>}
              </Box>
            }
          />
        )}
        {(expandedMode || error) && (
          <Banner variant="critical" title={errorText?.title} text={errorText?.description} />
        )}
        {(expandedMode || success) && (
          <Banner variant="success" title={successText?.title} text={successText?.description} />
        )}
        {!success && (
          <>
            {showNames && (
              <Box sx={{ display: "flex", gap: 6 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label={labelText?.firstName}
                    name="FirstName"
                    maxLength={50}
                    required={true}
                    value={firstName}
                    onChange={(e) => firstNameSet(e.currentTarget.value)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label={labelText?.lastName}
                    name="LastName"
                    maxLength={50}
                    required={true}
                    value={lastName}
                    onChange={(e) => lastNameSet(e.currentTarget.value)}
                  />
                </Box>
              </Box>
            )}

            {showTitle && (
              <Box sx={{ color: "text-default" }}>
                <TextField
                  label={labelText?.title}
                  name="Title"
                  maxLength={100}
                  required={false}
                  value={title}
                  onChange={(e) => titleSet(e.currentTarget.value)}
                />
              </Box>
            )}

            {specialtyField && <Box sx={{ color: "text-default" }}>{specialtyField}</Box>}

            {!stateAfterEmail && stateField && <Box>{stateField}</Box>}

            <Box
              sx={{
                color: "text-default",
                display: "flex",
                flexDirection: ["column", null, "row"],
                gap: 2
              }}
            >
              <Box sx={{ flex: 1 }}>{emailField}</Box>
              {isEmailOnly && (
                <Button
                  brand="secondary"
                  size="lg"
                  sx={{ minWidth: 40 }}
                  type="submit"
                  onClick={handleSubmitButtonClick}
                >
                  {buttonText}
                </Button>
              )}
            </Box>

            {stateAfterEmail && stateField && <Box>{stateField}</Box>}

            {showOrganization && (
              <Box sx={{ color: "text-default" }}>
                {showOrganizationDropdown ? (
                  <Dropdown
                    id="newsletter-organization-dropdown"
                    dropdownOptions={organizationOptions}
                    label={labelText?.organization}
                    required={true}
                    showMenuOverride={expandedMode}
                    wrapOptions={true}
                    onSelectOption={(option) => organizationSet(String(option.value ?? ""))}
                  />
                ) : (
                  <TextField
                    label={labelText?.organization}
                    maxLength={100}
                    name="Organization"
                    required={true}
                    value={organization}
                    onChange={(e) => organizationSet(e.currentTarget.value)}
                  />
                )}
              </Box>
            )}

            {showResponsibility && (
              <Box sx={{ color: "text-default" }}>
                <Dropdown
                  id="newsletter-responsibility-dropdown"
                  dropdownOptions={responsibilityOptions}
                  label={labelText?.responsibility}
                  required={false}
                  showMenuOverride={expandedMode}
                  wrapOptions={true}
                  onSelectOption={(option) => responsibilitySet(option.value)}
                />
              </Box>
            )}

            {showRequest && (
              <Box sx={{ color: "text-default" }}>
                <TextArea
                  label={labelText?.request}
                  name="Request"
                  rows={4}
                  required={false}
                  value={request}
                  onChange={(e) => requestSet(e.currentTarget.value)}
                />
              </Box>
            )}

            {showPostalCode && (
              <Box sx={{ color: "text-default" }}>
                <TextField
                  label={labelText?.zipCode}
                  maxLength={10}
                  name="ZipCode"
                  value={postalCode}
                  onChange={(e) => postalCodeSet(e.currentTarget.value)}
                />
              </Box>
            )}

            {showConsent && (
              <Box sx={{ mt: 1 }}>
                <Checkbox
                  label={consentText}
                  name="Consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.currentTarget.checked)}
                />
              </Box>
            )}

            {(showMedicalOnlyConsent ||
              showMedicalAndCommercialOnlyConsent ||
              showCommercialOnlyConsent) &&
              (optionalConsentText?.title ||
                optionalConsentText?.description ||
                optionalConsentText?.label) && (
                <Box
                  sx={{
                    color: "text-default",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 1
                  }}
                >
                  {optionalConsentText?.title && (
                    <Box sx={{ fontWeight: "font-weight-bold" }}>{optionalConsentText.title}</Box>
                  )}
                  {optionalConsentText?.description && <Box>{optionalConsentText.description}</Box>}
                  {optionalConsentText?.label && <Box>{optionalConsentText.label}</Box>}
                </Box>
              )}

            {showMedicalOnlyConsent && (
              <Box sx={{ mt: 1 }}>
                <Checkbox
                  label={labelText?.medicalOnlyConsent}
                  name="MedicalOnlyConsent"
                  checked={medicalOnlyConsent}
                  onChange={(e) => setMedicalOnlyConsent(e.currentTarget.checked)}
                />
              </Box>
            )}

            {showMedicalAndCommercialOnlyConsent && (
              <Box sx={{ mt: showMedicalOnlyConsent ? -2 : 1 }}>
                <Checkbox
                  label={labelText?.medicalAndCommercialOnlyConsent}
                  name="MedicalAndCommercialOnlyConsent"
                  checked={medicalAndCommercialOnlyConsent}
                  onChange={(e) => setMedicalAndCommercialOnlyConsent(e.currentTarget.checked)}
                />
              </Box>
            )}

            {showCommercialOnlyConsent && (
              <Box
                sx={{ mt: showMedicalOnlyConsent || showMedicalAndCommercialOnlyConsent ? -2 : 1 }}
              >
                <Checkbox
                  label={labelText?.commercialOnlyConsent}
                  name="CommercialOnlyConsent"
                  checked={commercialOnlyConsent}
                  onChange={(e) => setCommercialOnlyConsent(e.currentTarget.checked)}
                />
              </Box>
            )}

            {privacyText && !privacyAfterSubmit && (
              <Box sx={{ color: "text-default" }}>{privacyText}</Box>
            )}

            {!isEmailOnly && (
              <Box sx={{ textAlign: "center" }}>
                <Button
                  brand="secondary"
                  size="lg"
                  sx={{ minWidth: 50 }}
                  type="submit"
                  onClick={handleSubmitButtonClick}
                >
                  {buttonText}
                </Button>
              </Box>
            )}

            {privacyText && privacyAfterSubmit && (
              <Box sx={{ color: "text-default" }}>{privacyText}</Box>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "center",
          left: 0,
          opacity: loading ? 0.8 : 0,
          pointerEvents: loading ? "auto" : "none",
          position: "absolute",
          top: 0,
          transition: "transition-color-slow",
          width: "100%"
        }}
      >
        <Spinner />
      </Box>
    </Box>
  );
};
